import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { deleteReservation, fetchMolliePayment, fetchPartialPaymentInfo, fetchTransactionByPaymentId, putPartialPaymentReservation, putReservation, putTransaction, resetMollieFetchResults } from './mollieSlice';
import ContentLoader from '../subcomponents/ContentLoader';

export default function MollieUpdate() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { id } = useParams();

    const { user } = useSelector((state) => state.user);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const {
        transaction,
        transactionStatus,
        molliePayment,
        molliePaymentStatus,
        updateTransactionStatus,
        confirmReservationStatus,
        deleteReservationStatus,
        partialPaymentInfo,
        partialPaymentInfoStatus,
        confirmPartialPaymentReservationStatus
    } = useSelector((state) => state.mollie);

    const [hasUpdated, setHasUpdated] = useState(false);

    // Reset Mollie fetch results
    useEffect(() => {
        dispatch(resetMollieFetchResults());
    }, []);

    // Fetch ideal transaction from DB
    useEffect(() => {
        dispatch(fetchTransactionByPaymentId(id));
    }, [id]);

    // Fetch Mollie payment from Mollie if status is open, authorized or pending
    useEffect(() => {
        if (transactionStatus === "success" && (
            transaction?.status?.toLowerCase() === "open" ||
            transaction?.status?.toLowerCase() === "authorized" ||
            transaction?.status?.toLowerCase() === "pending")
        ) {
            dispatch(fetchMolliePayment(transaction.transactieId));
        }
    }, [transactionStatus]);

    // Update ideal transaction in DB with data from Mollie
    useEffect(() => {
        if (molliePaymentStatus === "success" && !hasUpdated) {
            setHasUpdated(true);
            dispatch(putTransaction({
                idealKoppelingId: id,
                status: molliePayment.status,
                modifiedBy: `AP/Mollie Update`,
                amount: molliePayment.amount.value
            }));
        }
    }, [molliePaymentStatus]);

    // Fetch ideal transaction from DB if above update is successful
    useEffect(() => {
        if (updateTransactionStatus === "success") {
            dispatch(fetchTransactionByPaymentId(id));
        }
    }, [updateTransactionStatus]);


    useEffect(() => {
        if (transactionStatus === "success" && hasUpdated) {
            // Update reservation details if ideal transaction status is open or status is failed/canceled/expired and payment delay is enabled
            if (
                transaction?.isDeelbetaling === false && (
                    transaction?.status?.toLowerCase() === "open" ||
                    (
                        (
                            transaction?.status?.toLowerCase() === "failed" ||
                            transaction?.status?.toLowerCase() === "canceled" ||
                            transaction?.status?.toLowerCase() === "expired"
                        ) && siomSettings.mollieEnableDelay
                    )
                )
            ) {
                dispatch(putReservation({
                    SubscriptionId: transaction?.inschrijvingId,
                    PerformanceId: transaction?.onderwijsUitvoeringId,
                    StudentId: transaction?.studentId,
                    IsReservation: true, // Updates subscription details, but it stays as a reservation
                    ModifiedBy: `AP/Mollie Update`,
                }));
            }

            // Confirm reservation if ideal transaction status is paid
            if (transaction?.isDeelbetaling === false && transaction?.status?.toLowerCase() === "paid") {
                dispatch(putReservation({
                    SubscriptionId: transaction?.inschrijvingId,
                    PerformanceId: transaction?.onderwijsUitvoeringId,
                    StudentId: transaction?.studentId,
                    IsReservation: false,
                    ModifiedBy: `AP/Mollie Update`,
                }));
            }

            // Delete reservation if ideal transaction status is failed/canceled/expired and payment delay is disabled
            if (
                transaction?.isDeelbetaling === false &&
                (
                    transaction?.status?.toLowerCase() === "failed" ||
                    transaction?.status?.toLowerCase() === "canceled" ||
                    transaction?.status?.toLowerCase() === "expired"
                )
                && !siomSettings.mollieEnableDelay
            ) {
                dispatch(deleteReservation({
                    SubscriptionId: transaction?.inschrijvingId,
                    PerformanceId: transaction?.onderwijsUitvoeringId,
                    StudentId: transaction?.studentId,
                    IsReservation: true,
                    ModifiedBy: `AP/Mollie Update`,
                }));
            }

            // Get partial payment info
            if (transaction?.isDeelbetaling === true) {
                dispatch(fetchPartialPaymentInfo({
                    subscriptionId: transaction?.inschrijvingId,
                    performanceId: transaction?.onderwijsUitvoeringId,
                    studentId: transaction?.studentId
                }));
            }
        }
    }, [transaction, transactionStatus, molliePaymentStatus]);

    // Update partial payment reservation info
    useEffect(() => {
        if (transaction?.isDeelbetaling === true && partialPaymentInfoStatus === 'success') {
            dispatch(putPartialPaymentReservation({
                performanceId: transaction?.onderwijsUitvoeringId,
                subscriptionId: transaction?.inschrijvingId,
                studentId: transaction?.studentId
            }));
        }
    }, [partialPaymentInfoStatus])

    useEffect(() => {
        if ((confirmReservationStatus === "success" || deleteReservationStatus === "success" || confirmPartialPaymentReservationStatus === "success") && user) {
            navigate(`/inschrijven/betaling/${id}`);
        }
    }, [confirmReservationStatus, deleteReservationStatus, confirmPartialPaymentReservationStatus])

    return (
        <Box sx={{
            display: 'flex', flex: '1 1 auto', justifyContent: 'center', alignItems: 'center',
            backgroundImage: `radial-gradient(circle at center, white 20%, rgba(0, 255, 255, 0.1) 100%)`
        }}>
            <Card sx={{ width: { xs: '90%', md: '50%' } }}>
                <CardContent>
                    <Stack>
                        {transactionStatus === "success" ? (
                            <>
                                <>
                                    {transaction?.status?.toLowerCase() === "paid" &&
                                        <Typography variant="h6" align="center" gutterBottom>
                                            {resourceFinal[appLanguage].messages.PAYMENT_PAID_NON_USER}
                                        </Typography>
                                    }

                                    {transaction?.status?.toLowerCase() === "open" &&
                                        <Typography variant="h6" align="center" gutterBottom>
                                            {resourceFinal[appLanguage].messages.PAYMENT_OPEN_NON_USER}
                                        </Typography>
                                    }

                                    {(transaction?.status?.toLowerCase() !== "paid" && transaction?.status?.toLowerCase() !== "open") &&
                                        <Typography variant="h6" align="center" gutterBottom>
                                            {resourceFinal[appLanguage].messages.PAYMENT_NOT_PAID_NON_USER}
                                        </Typography>
                                    }
                                </>
                                {transaction?.isDeelbetaling === true &&
                                    <>
                                        <ContentLoader
                                            successCondition={partialPaymentInfoStatus === 'success'}
                                            errorCondition={partialPaymentInfoStatus === 'failed'}
                                            errorCompact
                                            content={
                                                <>
                                                    <Typography variant="h6" align="center" gutterBottom>
                                                        {resourceFinal[appLanguage].words.PARTIAL_PAYMENTS} {resourceFinal[appLanguage].words.PARTIAL_PAYMENT_COMPLETED.toLowerCase()}: {partialPaymentInfo?.partialPaymentCompleted}/{partialPaymentInfo?.partialPaymentAmount}
                                                    </Typography>

                                                    {partialPaymentInfo?.allPartialPaymentsCompleted &&
                                                        <ContentLoader
                                                            successCondition={confirmPartialPaymentReservationStatus === 'success'}
                                                            errorCondition={confirmPartialPaymentReservationStatus === 'failed'}
                                                            errorCompact
                                                            content={
                                                                <Typography variant="h6" align="center" gutterBottom>
                                                                    {resourceFinal[appLanguage].messages.PAYMENT_PARTIAL_RESERVATION_UPDATED}
                                                                </Typography>
                                                            }
                                                        />
                                                    }
                                                </>
                                            }
                                        />
                                    </>
                                }
                            </>
                        ) : (
                            <Typography variant="h6" align="center" gutterBottom>
                                {resourceFinal[appLanguage].messages.PAYMENT_WAITING}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}
